import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react'
import { Card, CardContent, cn } from '@saas-pages/shared/ui'
import { localeCode, useLocale, useT } from '@/shared/i18n'
import {
  calculateShiftCalendar,
  SHIFT_CALENDAR_MAX_CYCLE_DAYS,
  SHIFT_CALENDAR_MAX_HOURS,
  SHIFT_CALENDAR_MAX_YEAR,
  SHIFT_CALENDAR_MIN_CYCLE_DAYS,
  SHIFT_CALENDAR_MIN_HOURS,
  SHIFT_CALENDAR_MIN_YEAR,
  type ShiftCalendarCalculation,
  type ShiftCalendarDay,
  type ShiftCalendarInput,
  type ShiftPreset,
} from '@/pages/shift-calendar/model/shiftCalendar'
import { startShiftCalendarOperation } from '@/pages/shift-calendar/model/shiftCalendarAnalytics'

const PRERENDER_YEAR = 2026
const PRESETS: readonly ShiftPreset[] = ['2_2', '3_3', '1_3', 'custom']

export function ShiftCalendarPage() {
  const t = useT()
  const { locale } = useLocale()
  const [input, setInput] = useState<ShiftCalendarInput>({
    preset: '2_2',
    firstWorkDate: '2026-01-01',
    year: PRERENDER_YEAR,
    customWorkDays: 2,
    customRestDays: 2,
    shiftHours: 12,
  })

  useEffect(() => {
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0')
    const day = String(today.getDate()).padStart(2, '0')
    setInput((current) => ({
      ...current,
      year,
      firstWorkDate: `${year}-${month}-${day}`,
    }))
  }, [])

  const calculation = useMemo(() => calculateShiftCalendar(input), [input])
  const monthNames = useMemo(
    () => Array.from({ length: 12 }, (_, index) => new Intl.DateTimeFormat(
      localeCode(locale),
      { month: 'long', timeZone: 'UTC' },
    ).format(new Date(Date.UTC(PRERENDER_YEAR, index, 1)))),
    [locale],
  )

  const recordInteraction = useCallback((nextInput: ShiftCalendarInput) => {
    const operation = startShiftCalendarOperation(nextInput.preset)
    const startedAt = performance.now()
    const nextCalculation = calculateShiftCalendar(nextInput)
    if (nextCalculation.error) {
      operation.error(
        nextCalculation.error === 'unsupported_year' || nextCalculation.error === 'invalid_cycle'
          ? 'unsupported_input'
          : 'invalid_input',
      )
      return
    }
    operation.success(performance.now() - startedAt)
  }, [])

  const updateInput = useCallback(<Key extends keyof ShiftCalendarInput>(
    key: Key,
    value: ShiftCalendarInput[Key],
    track = false,
  ) => {
    const next = { ...input, [key]: value }
    setInput(next)
    if (track) recordInteraction(next)
  }, [input, recordInteraction])

  return (
    <div className="mx-auto max-w-7xl">
      <section className="pb-5 pt-2">
        <div className="mb-2 inline-flex rounded-md border bg-card px-2.5 py-1 text-xs font-medium text-muted-foreground shadow-sm">
          {t.common.localBadge}
        </div>
        <h1 className="text-3xl font-bold tracking-normal sm:text-4xl">
          {t.shiftCalendar.title}
        </h1>
        <p className="mt-2 max-w-3xl text-base leading-7 text-muted-foreground">
          {t.shiftCalendar.description}
        </p>
      </section>

      <Card className="ym-hide-content ym-disable-keys overflow-hidden">
        <div aria-hidden="true" className="h-1.5 bg-gradient-to-r from-emerald-400 via-primary to-cyan-400" />
        <CardContent className="grid gap-0 p-0 lg:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]">
          <div className="space-y-4 border-b p-4 sm:p-5 lg:border-b-0 lg:border-r">
            <fieldset>
              <legend className="mb-2 text-sm font-semibold">{t.shiftCalendar.presetTitle}</legend>
              <div className="grid grid-cols-2 gap-2">
                {PRESETS.map((preset) => (
                  <label
                    key={preset}
                    className={cn(
                      'cursor-pointer rounded-md border px-3 py-2 text-sm transition-colors',
                      input.preset === preset
                        ? 'border-primary/50 bg-primary/10 font-medium'
                        : 'bg-background/70 hover:border-primary/40',
                    )}
                  >
                    <input
                      type="radio"
                      name="shift-preset"
                      value={preset}
                      checked={input.preset === preset}
                      onChange={() => updateInput('preset', preset, true)}
                      className="sr-only"
                    />
                    {t.shiftCalendar.presets[preset]}
                  </label>
                ))}
              </div>
            </fieldset>

            {input.preset === 'custom' && (
              <div className="grid grid-cols-2 gap-3">
                <NumberField
                  id="shift-work-days"
                  label={t.shiftCalendar.customWorkDays}
                  value={input.customWorkDays}
                  min={SHIFT_CALENDAR_MIN_CYCLE_DAYS}
                  max={SHIFT_CALENDAR_MAX_CYCLE_DAYS}
                  onChange={(value) => updateInput('customWorkDays', value)}
                  onBlur={() => recordInteraction(input)}
                />
                <NumberField
                  id="shift-rest-days"
                  label={t.shiftCalendar.customRestDays}
                  value={input.customRestDays}
                  min={SHIFT_CALENDAR_MIN_CYCLE_DAYS}
                  max={SHIFT_CALENDAR_MAX_CYCLE_DAYS}
                  onChange={(value) => updateInput('customRestDays', value)}
                  onBlur={() => recordInteraction(input)}
                />
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-3">
              <label htmlFor="shift-anchor" className="block">
                <span className="mb-1.5 block text-sm font-medium">{t.shiftCalendar.firstWorkDate}</span>
                <input
                  id="shift-anchor"
                  type="date"
                  value={input.firstWorkDate}
                  min={`${SHIFT_CALENDAR_MIN_YEAR}-01-01`}
                  max={`${SHIFT_CALENDAR_MAX_YEAR}-12-31`}
                  onChange={(event) => updateInput('firstWorkDate', event.target.value)}
                  onBlur={() => recordInteraction(input)}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                />
              </label>
              <NumberField
                id="shift-year"
                label={t.shiftCalendar.year}
                value={input.year}
                min={SHIFT_CALENDAR_MIN_YEAR}
                max={SHIFT_CALENDAR_MAX_YEAR}
                onChange={(value) => updateInput('year', value)}
                onBlur={() => recordInteraction(input)}
              />
              <NumberField
                id="shift-hours"
                label={t.shiftCalendar.shiftHours}
                value={input.shiftHours}
                min={SHIFT_CALENDAR_MIN_HOURS}
                max={SHIFT_CALENDAR_MAX_HOURS}
                onChange={(value) => updateInput('shiftHours', value)}
                onBlur={() => recordInteraction(input)}
              />
            </div>
            <p className="text-xs leading-5 text-muted-foreground">{t.shiftCalendar.limits}</p>
          </div>

          <Summary calculation={calculation} year={input.year} />
        </CardContent>
      </Card>

      {calculation.result && (
        <section className="ym-hide-content mt-6" aria-labelledby="shift-calendar-year">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h2 id="shift-calendar-year" className="text-2xl font-semibold tracking-normal">
              {t.shiftCalendar.calendarTitle.replace('{year}', String(input.year))}
            </h2>
            <div className="flex gap-4 text-xs text-muted-foreground">
              <Legend swatch="bg-primary text-primary-foreground" label={t.shiftCalendar.workDay} />
              <Legend swatch="bg-muted" label={t.shiftCalendar.restDay} />
            </div>
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {monthNames.map((monthName, index) => (
              <Month
                key={monthName}
                name={monthName}
                days={calculation.result!.days.filter((day) => day.month === index + 1)}
                weekdays={t.shiftCalendar.weekdays}
                workDayLabel={t.shiftCalendar.workDay}
                restDayLabel={t.shiftCalendar.restDay}
              />
            ))}
          </div>
        </section>
      )}

      <aside className="mt-6 rounded-lg border border-amber-400/40 bg-amber-100/50 p-4 text-sm leading-6 text-amber-950 dark:bg-amber-950/20 dark:text-amber-100">
        <h2 className="font-semibold">{t.shiftCalendar.disclaimerTitle}</h2>
        <p className="mt-1">{t.shiftCalendar.disclaimer}</p>
        <a
          href="/proizvodstvennyj-kalendar"
          className="mt-2 inline-flex font-medium underline underline-offset-4"
        >
          {t.shiftCalendar.productionCalendarLink}
        </a>
        <p className="mt-2 text-xs opacity-80">{t.shiftCalendar.privacy}</p>
      </aside>

      <section className="mt-10 border-t pt-8">
        <h2 className="text-2xl font-semibold tracking-normal">{t.shiftCalendar.howTitle}</h2>
        <ol className="mt-4 grid gap-3 md:grid-cols-2">
          {t.shiftCalendar.howTo.map((step, index) => (
            <li key={step} className="rounded-lg border bg-card p-4 text-sm leading-6 shadow-sm">
              <span className="mr-2 font-bold text-primary">{index + 1}.</span>{step}
            </li>
          ))}
        </ol>
      </section>

      <section className="mt-10 border-t pt-8">
        <h2 className="text-2xl font-semibold tracking-normal">{t.shiftCalendar.faqTitle}</h2>
        <dl className="mt-4 grid gap-3 lg:grid-cols-3">
          {t.shiftCalendar.faq.map((item) => (
            <div key={item.question} className="rounded-lg border bg-card p-5 shadow-sm">
              <dt className="font-semibold">{item.question}</dt>
              <dd className="mt-2 text-sm leading-6 text-muted-foreground">{item.answer}</dd>
            </div>
          ))}
        </dl>
      </section>
    </div>
  )
}

function Summary({
  calculation,
  year,
}: {
  calculation: ShiftCalendarCalculation
  year: number
}) {
  const t = useT()
  if (calculation.error) {
    return (
      <div className="flex items-center p-5 sm:p-6">
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {t.shiftCalendar.errors[calculation.error]}
        </p>
      </div>
    )
  }

  const metrics = [
    [t.shiftCalendar.workShifts, calculation.result.workShifts],
    [t.shiftCalendar.restDays, calculation.result.restDays],
    [t.shiftCalendar.totalHours, calculation.result.totalHours],
  ] as const
  return (
    <div className="bg-muted/30 p-4 sm:p-5">
      <h2 className="text-sm font-semibold">
        {t.shiftCalendar.summaryTitle.replace('{year}', String(year))}
      </h2>
      <div className="mt-3 grid grid-cols-3 gap-2 lg:grid-cols-1">
        {metrics.map(([label, value]) => (
          <div key={label} className="rounded-lg border bg-background/80 p-3 shadow-sm">
            <p className="text-xl font-bold tabular-nums sm:text-2xl">{value}</p>
            <p className="mt-1 text-xs leading-4 text-muted-foreground">{label}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

function NumberField({
  id,
  label,
  value,
  min,
  max,
  onChange,
  onBlur,
}: {
  id: string
  label: string
  value: number
  min: number
  max: number
  onChange: (value: number) => void
  onBlur: () => void
}) {
  return (
    <label htmlFor={id} className="block">
      <span className="mb-1.5 block text-sm font-medium">{label}</span>
      <input
        id={id}
        type="number"
        value={value}
        min={min}
        max={max}
        step={1}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(Number(event.target.value))}
        onBlur={onBlur}
        className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
      />
    </label>
  )
}

function Month({
  name,
  days,
  weekdays,
  workDayLabel,
  restDayLabel,
}: {
  name: string
  days: readonly ShiftCalendarDay[]
  weekdays: readonly string[]
  workDayLabel: string
  restDayLabel: string
}) {
  const blanks = days[0] ? (days[0].weekday + 6) % 7 : 0
  return (
    <article className="rounded-lg border bg-card p-3 shadow-sm">
      <h3 className="mb-3 text-center font-semibold capitalize">{name}</h3>
      <div className="grid grid-cols-7 gap-1 text-center">
        {weekdays.map((weekday) => (
          <span key={weekday} className="pb-1 text-[0.65rem] font-medium text-muted-foreground">
            {weekday}
          </span>
        ))}
        {Array.from({ length: blanks }, (_, index) => <span key={`blank-${index}`} />)}
        {days.map((day) => (
          <span
            key={day.date}
            title={`${day.date}: ${day.isWorkDay ? workDayLabel : restDayLabel}`}
            className={cn(
              'flex aspect-square items-center justify-center rounded text-xs tabular-nums',
              day.isWorkDay
                ? 'bg-primary font-semibold text-primary-foreground'
                : 'bg-muted text-muted-foreground',
            )}
          >
            {day.day}
          </span>
        ))}
      </div>
    </article>
  )
}

function Legend({ swatch, label }: { swatch: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn('h-3 w-3 rounded-sm', swatch)} />
      {label}
    </span>
  )
}
